import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';

// --- LAYOUT AND AUTHENTICATION ---
import Footer from './components/Footer';
import PrivateRoute from './pages/PrivateRoute';

// --- PAGE COMPONENTS (Only the ones being actively rendered are needed) ---
// Public Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

const JoinTeamPage = lazy(() => import('./pages/JoinTeamPage'));
const OrganizationSetupPage = lazy(() => import('./pages/OrganizationSetupPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CookiePage = lazy(() => import('./pages/CookiePage'));

// Admin Portal Pages
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const TeamSelectorPage = lazy(() => import('./pages/TeamSelectorPage'));

/**
 * PageLayout Component
 * A wrapper for public-facing pages that includes the site Footer.
 */
const PageLayout = () => {
  return (
    <div className="flex flex-col min-h-screen bg-cool-gray-100 font-sans">
      <main className="flex-grow">
        <Outlet /> {/* Renders the nested route's component */}
      </main>
      <Footer />
    </div>
  );
};


/**
 * App Component
 * The main component that defines all application routes.
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* === PUBLIC ROUTES (Accessible to everyone) === */}
          <Route element={<PageLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/register" element={<SignupPage />} />
            <Route path="/join-team" element={<JoinTeamPage />} />
            <Route path="/setup-organization" element={
              <PrivateRoute>
                <OrganizationSetupPage />
              </PrivateRoute>
            } />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/cookie-policy" element={<CookiePage />} />

          </Route>

          {/* === PRIVATE ADMIN ROUTES (The only allowed private section) === */}
          
          {/* Main Admin Dashboard - uses TeamSelectorPage for team selection */}
          <Route path="/admin/dashboard" element={
              <PrivateRoute>
                <TeamSelectorPage />
              </PrivateRoute>
            } />
          
          {/* Team-specific dashboard route */}
          <Route path="/admin/dashboard/:orgId/:teamId" element={
              <PrivateRoute>
                <AdminDashboardPage />
              </PrivateRoute>
            } />
          
          {/* Organization dashboard without team */}
          <Route path="/admin/dashboard/:orgId" element={
              <PrivateRoute>
                <AdminDashboardPage />
              </PrivateRoute>
            } />
          {/* Catch-all for other admin routes */}
          <Route 
            path="/admin/dashboard/:orgId/*" 
            element={
              <PrivateRoute>
                <AdminDashboardPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/dashboard/:orgId/employees" 
            element={
              <PrivateRoute>
                <AdminDashboardPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/dashboard/:orgId/settings" 
            element={
              <PrivateRoute>
                <AdminDashboardPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/dashboard/register" 
            element={
              <PrivateRoute>
                <AdminDashboardPage />
              </PrivateRoute>
            } 
          />

          {/* === REDIRECTS FOR LEGACY & DISABLED ROUTES === */}
          
          {/* Any attempt to access old private routes now redirects to the homepage. */}
          {/* The PrivateRoute wrapper is not needed because the destination is public. */}
          <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/organization-register" element={<Navigate to="/organization-signup" replace />} />

          {/* === CATCH-ALL REDIRECT (For any other unknown URL) === */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;